import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GridFilterModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';

import { ActionPermissions, CustomPermission, ListType } from '../../../@types';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { ShipmentListResponse } from '../../../@types/shipment.type';
import { HorizontalAlignProps, InlineGroup } from '../../../components/atoms/InlineGroup';
import { Link } from '../../../components/atoms/Link';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { DropdownItem } from '../../../components/atoms/SelectMenu/SelectMenu.props';
import { Typography, TypographyVariantProps } from '../../../components/atoms/Typography';
import { CustomDataGrid } from '../../../components/layouts/CustomDataGrid';
import { PrintLabel } from '../../../components/molecules/PintLabel';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../../configs/common';
import { ShipmentTypes, GridFeatureModeConstant, StructureTypeCategory } from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import { shipmentListColumnSelectionItems } from '../../../constants/shipment';
import { TabHandler } from '../../../handlers/TabHandler';
import { useGetAllShipments } from '../../../queries/shipment-query';
import { useGetStructureTypesByCategoryCode } from '../../../queries/structure-type-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { GridContainer, Wrapper } from '../../../styled/common.styles';
import { checkListPagetFilterAvailability, formatDate } from '../../../utils/common';

export const ShipmentList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [shipments, getShipments] = useState<ShipmentListResponse>();
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [structureTypeList, setStructureTypeList] = useState<DropdownItem[]>();
  
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedColumnSelectionItems, setSelectedColumnSelectionItems] = useState<ColumnSelectionItem[]>(shipmentListColumnSelectionItems);

  const getAllShipments = useGetAllShipments({ filterModel, page, pageSize, sortModel }, selectedColumnSelectionItems);
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const { data: structureTypeListData } = useGetStructureTypesByCategoryCode(StructureTypeCategory.Operational);

  TabHandler(`List Shipments | ${PLATFORM_NAME}`);
  
  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    if (structureTypeListData) {
      setStructureTypeList(structureTypeListData.map((listData) => (
        { value: listData.name, label: listData.name }
      )));
    }
  }, [structureTypeListData]);

  useEffect(() => {
    getAllShipments.data && getShipments(getAllShipments.data);
  }, [getAllShipments.data, getAllShipments.isRefetching]);

  useEffect(() => {
    if (filterModel.items.length === 0 || checkListPagetFilterAvailability(filterModel) || sortModel?.some(sort => sort.field)) {
      getAllShipments.refetch();  
    }
  }, [filterModel, sortModel]);

  useEffect(() => {
    if(page || pageSize) {
      getAllShipments.refetch();
    }
  }, [page, pageSize, selectedColumnSelectionItems]);

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    setFilterModel({ ...filterModel });
  }, []);
  
  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  const handleColumnsChange = (items: ColumnSelectionItem[]) => {
    setSelectedColumnSelectionItems(items);
  };

  useEffect(() => {
    setColumns([
      {
        field: 'id',
        headerName: 'Shipment ID',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        renderCell: (params: GridRenderCellParams) => {
          if (!isUserHasPermission(ActionPermissions.Shipment_View_Add_Stock, getUserPermissionsQuery.data) && params.row.shipmentTypeCode === ShipmentTypes.AddStock) {
            return params.row.id;
          } else if (!isUserHasPermission(ActionPermissions.Shipment_View_Return, getUserPermissionsQuery.data) && params.row.shipmentTypeCode === ShipmentTypes.Return) {
            return params.row.id;
          } else if (!isUserHasPermission(ActionPermissions.Shipment_View_Dispatch, getUserPermissionsQuery.data) && (params.row.shipmentTypeCode === ShipmentTypes.Dispatch || params.row.shipmentTypeCode === ShipmentTypes.InternalTransfer)) {
            return params.row.id;
          } else if (params.row.shipmentTypeCode === ShipmentTypes.Return) {
            return <Link href={`shipments/return/${params.row.id}`}>{params.row.id}</Link>;
          } else {
            return <Link href={`shipments/${params.row.id}`}>{params.row.id}</Link>;
          }
        }
      },
      {
        field: 'shipmentTypeCode',
        headerName: 'Type',
        width: 150,
        type: 'singleSelect',
        valueOptions: [
          { value: 'add-stock', label: 'Add Stock' },
          { value: 'dispatch', label: 'Dispatch' },
          { value: 'return', label: 'Return' },
          { value: 'internal-transfer', label: 'Internal Transfer' }
        ],
        renderCell: (params: GridRenderCellParams) => params.row.shipmentTypeName
      },
      {
        field: 'shipmentStatusCode',
        headerName: 'Status',
        width: 150,
        type: 'singleSelect',
        valueOptions: [
          { value: 'pending', label: 'Pending' },
          { value: 'partial', label: 'Partial' },
          { value: 'complete', label: 'Complete' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'in-transit', label: 'In Transit' },
          { value: 'received', label: 'Received' }
        ],
        renderCell: (params: GridRenderCellParams) => params.row.shipmentStatusName
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'date',
        width: 150,
        valueGetter: (_value, row) => new Date(row.createdAt || ''),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'createdByName',
        headerName: 'Created By',
        type: 'string',
        width: 200
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        type: 'date',
        width: 150,
        valueGetter: (_value, row) => row?.updatedAt && new Date(row.updatedAt || ''),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'updatedByName',
        headerName: 'Updated By',
        type: 'string',
        width: 200
      },
      {
        field: 'completedAt',
        headerName: 'Completed At',
        type: 'date',
        width: 150,
        valueGetter: (_value, row) => row?.completedAt && new Date(row.completedAt || ''),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'sourceName',
        headerName: 'Source',
        type: 'string',
        width: 200
      },
      {
        field: 'sourceNodeType',
        headerName: 'Source Node Type',
        type: 'singleSelect',
        width: 200,   
        valueGetter: (_value, row) => row.sourceNodeType,
        valueOptions: structureTypeList
      },
      {
        field: 'destinationName',
        headerName: 'Destination',
        type: 'string',
        width: 200
      },
      {
        field: 'destinationNodeType',
        headerName: 'Destination Node Type',
        type: 'singleSelect',
        width: 200,   
        valueGetter: (_value, row) => row.destinationNodeType,
        valueOptions: structureTypeList
      },
      {
        field: 'clientContractNames',
        headerName: 'Client/Contract',
        type: 'string',
        width: 200
      },
      {
        field: 'notes',
        headerName: 'Notes',
        type: 'string',
        width: 300
      },
      {
        field: 'referenceNumber1',
        headerName: 'Reference 1',
        type: 'string',
        width: 150
      },
      {
        field: 'referenceNumber2',
        headerName: 'Reference 2',
        type: 'string',
        width: 150
      },
      {
        field: 'referenceNumber3',
        headerName: 'Reference 3',
        type: 'string',
        width: 150
      },
      {
        field: 'qtyTotal',
        headerName: 'Quantity Total',
        width: 150,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'qtyReceived',
        headerName: 'Quantity Received',
        width: 150,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'qtyPending',
        headerName: 'Quantity Pending',
        width: 150,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'courierName',
        headerName: 'Courier Name',
        type: 'string',
        width: 200
      },
      {
        field: 'connoteNo',
        headerName: 'Connote No.',
        type: 'string',
        width: 200
      },
      {
        field: 'latestTrackingStatus',
        headerName: 'Latest Tracking Event',
        type: 'string',
        width: 200
      },
      {
        field: 'arrivalDate',
        headerName: 'Expected Arrival Date',
        type: 'date',
        width: 200,
        valueGetter: (_value, row) => row?.arrivalDate && new Date(row.arrivalDate || ''),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'shippingCost',
        headerName: 'Shipping Cost',
        width: 150,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'printLabel',
        headerName: 'Print Label',
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <PrintLabel entityId={params.row.id} entityType={PrintLabelEntityTypes.Shipment}/>
          );
        },
        filterable: false
      }
    ]);
  }, [structureTypeList]);

  return (
    <PageContainer>
      <Wrapper>
        <InlineGroup horizontal={HorizontalAlignProps.Between}>
          <Typography variant={TypographyVariantProps.H5} fontWeight={600}>
            List Shipments
          </Typography>
        </InlineGroup>
        <GridContainer>
          <CustomDataGrid
            id="shipment-list"
            columns={columns}
            rows={shipments?.data || []}
            rowCount={shipments?.total || 0}
            exportFileName={ListType.ShipmentList}
            enableCSVExport={isUserHasPermission(ActionPermissions.Shipment_View_Search, permissions)}
            columnSelectionItems={selectedColumnSelectionItems}
            filterMode={GridFeatureModeConstant.server}
            filterModel={filterModel}
            onFilterModelChange={(filterModel: GridFilterModel) => {
              onFilterChange(filterModel);
            }}
            handleColumnsChange={handleColumnsChange}
            loading={getAllShipments.isLoading || getAllShipments.isRefetching}
            rowsPerPageOptions={[25, 50, 100]}
            pagination={true}
            page={page}
            pageSize={pageSize}
            paginationMode={GridFeatureModeConstant.server}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            sortModel={sortModel}
            sortingMode={GridFeatureModeConstant.server}
            onSortModelChange={handleSortModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  parentNodeId: false,
                  referenceNumbers: false,
                  status: false,
                },
              },
            }}
            componentsProps={{
              toolbar: {
                printOptions: { disableToolbarButton: true }
              }
            }}
            getRowHeight={() => 'auto'}
            setColumns={setColumns}
          />
        </GridContainer>
      </Wrapper>
    </PageContainer>
  );
};